import {FormInstance} from 'antd/es/form';
import {
    isArray, isEqual,
} from 'lodash-es';
import {
    useEffect, useMemo, useRef, useState,
} from 'react';

import {IMetaActionInfo} from 'entities/metadata';
import {TEntity} from 'modules/data';
import {selectFilterEntityData} from 'modules/data/data-selectors';
import {IFilterEntityData} from 'modules/data/data-types';
import {useAppSelector} from 'store/config/hooks';

import {
    IFormButtonWatcher,
    EFormButtonWatcherActions,
    EFormButtonWatcherConditions, EFormButtonWatcherConnective,
} from './use-form-button-watcher-types';
import {createConnective} from './use-form-button-watcher.utils';

interface IFormValuesChangeEventDetail {
    values: { [i: string]: any };
    targetForm?: any;
    filterData?: TEntity;
}

interface IDispatchFormWatcherEventArgs extends IFormValuesChangeEventDetail {
}

const FORM_WATCHER_EVENT = 'form-changed';

export const dispatchFormWatcherEvent = (args: IDispatchFormWatcherEventArgs) => {
    window.dispatchEvent(new CustomEvent(FORM_WATCHER_EVENT, {detail: args}));
};

type TCreateActionHandlersArgs = {
    detail: IFormValuesChangeEventDetail;
    watcher: IFormButtonWatcher;
}

interface ICreateConditionCallbacksArgs {
    condition: EFormButtonWatcherConditions;
    targetValue: any;
    ownValue?: any;
    withKey?: string;
    filterData?: TEntity;
    allValues?: any;
}

interface IUseFormButtonWatcherArgs {
    actionsForm: IMetaActionInfo;
    form?: FormInstance<any>;
    entityName?: string;
}

export const useFormButtonWatcher = ({actionsForm, form, entityName}: IUseFormButtonWatcherArgs) => {
    const [isHidden, setIsHidden] = useState(false);
    const [isDisabled, setIsDisabled] = useState(false);
    const [shouldDispatch, setShouldDispatch] = useState<boolean>(false);

    const formValues = form?.getFieldsValue();
    const lastValues = useRef<{ [i: string]: any }>(formValues);
    const filterValue = useAppSelector(selectFilterEntityData(entityName || ''));
    const prevFilterValue = useRef<IFilterEntityData>(filterValue);

    const setLastValues = (newValues: { [i: string]: any }) => {
        lastValues.current = newValues;
        setShouldDispatch(true);
    };

    if (formValues && !isEqual(lastValues.current, formValues)) {
        setLastValues(formValues);
    }

    const checkIfWatcherIsTriggered = ({
        condition,
        targetValue, // значение поля, которое таргетит watcher
        withKey,
        filterData,
        allValues,
    }: ICreateConditionCallbacksArgs) => {
        const callbacks = {
            [EFormButtonWatcherConditions.unset]: !targetValue,
            [EFormButtonWatcherConditions.filterUnset]: !(withKey ? filterData?.[withKey] : filterData),
            [EFormButtonWatcherConditions.formHasData]: (() => {
                if (!allValues) return false;
                return Object.values(allValues).some(value => {
                    if (value === undefined || value === null || value === '') return false;
                    if (Array.isArray(value)) return value.length > 0;
                    if (typeof value === 'object') return Object.keys(value).length > 0;
                    return true;
                });
            })(),
        };
        return callbacks[condition];
    };

    const createActionHandlers = ({
        watcher, detail,
    }: TCreateActionHandlersArgs) => {
        const {
            conditions,
            perform: actions,
        } = watcher;
        const {values, filterData} = detail;
        let isWatcherTriggered: boolean | null = null;

        conditions?.forEach(({
            target,
            when: condition,
            withConnective,
            withKey,
        }) => {
            const targetValue = target ? values[target] : undefined;

            const isTriggered = checkIfWatcherIsTriggered({
                condition,
                targetValue,
                withKey,
                filterData,
                allValues: values,
            });
            const joinCondition = createConnective(withConnective);

            if (isWatcherTriggered === null) {
                isWatcherTriggered = withConnective === EFormButtonWatcherConnective.NOT
                    ? !isTriggered : isTriggered;
            } else isWatcherTriggered = joinCondition(isWatcherTriggered, isTriggered);
        });

        const handlers = {
            [EFormButtonWatcherActions.disable]: () => {
                if (isWatcherTriggered) setIsDisabled(true);
                else setIsDisabled(false);
            },
            [EFormButtonWatcherActions.hide]: () => {
                if (isWatcherTriggered) setIsHidden(true);
                else setIsHidden(false);
            },
        };

        setLastValues(values);

        if (isArray(actions)) return actions.map(action => handlers?.[action]);
        return [handlers?.[actions]];
    };

    const hasFilterCondition = useMemo(() => (
        actionsForm?.watchers?.some(watcher =>
            watcher.conditions?.some(condition =>
                condition.when === EFormButtonWatcherConditions.filterUnset
            )
        )
    ), [actionsForm]);

    useEffect(() => {
        const handler = ({detail}: any) => {
            if (!form && !hasFilterCondition) return;

            const {targetForm} = detail as IFormValuesChangeEventDetail;
            if (targetForm && form !== targetForm && !hasFilterCondition) return;

            actionsForm?.watchers?.forEach(watcher => {
                createActionHandlers({
                    watcher,
                    detail,
                }).forEach(actionHandler => {
                    actionHandler?.();
                });
            });
        };
        window.addEventListener(FORM_WATCHER_EVENT, handler);
        return () => {
            window.removeEventListener(FORM_WATCHER_EVENT, handler);
        };
    }, []);

    // Диспатчим событие при изменении фильтра
    useEffect(() => {
        if (!hasFilterCondition) return;

        if (!isEqual(prevFilterValue.current, filterValue)) {
            dispatchFormWatcherEvent({
                values: formValues,
                targetForm: form,
                filterData: filterValue?.data || {},
            });
            prevFilterValue.current = filterValue;
        }
    }, [filterValue, form, formValues]);

    useEffect(() => {
        if (shouldDispatch) {
            dispatchFormWatcherEvent({
                values: formValues,
                targetForm: form,
                filterData: filterValue?.data || {}
            });
            setShouldDispatch(false);
        }
    }, [shouldDispatch]);

    return {
        isHidden,
        isDisabled,
    };
};
